import { TaskTypeStatus, type TaskType } from '@econtractor/types';

export type KanbanDropAction =
    | { type: 'reorder' }
    | { type: 'status'; status: number; toastKey: string }
    | { type: 'openResponse'; toastKey: string }
    | { type: 'openReject'; toastKey: string }
    | { type: 'deny'; toastKey: string };

export type KanbanDropOptions = {
    /** When true, executor===controller answer goes straight to ACCEPTED */
    autoAcceptOwn?: boolean;
};

export const columnDroppableId = (status: number): string => `col-${status}`;

export const parseColumnDroppableId = (id: string | number): number | null => {
    const raw = String(id);
    if (!raw.startsWith('col-')) return null;
    const status = Number(raw.slice(4));
    return Number.isFinite(status) ? status : null;
};

export const resolveDropAction = (
    task: TaskType,
    fromStatus: number,
    toStatus: number,
    userId: number,
    options: KanbanDropOptions = {},
): KanbanDropAction => {
    if (fromStatus === toStatus) return { type: 'reorder' };

    const isExecutor = userId > 0 && userId === Number(task.taskExecutor);
    const isController = userId > 0 && userId === Number(task.taskController);
    const autoAcceptOwn = Boolean(options.autoAcceptOwn);

    if (fromStatus === TaskTypeStatus.NEW && toStatus === TaskTypeStatus.INWORK) {
        if (!isExecutor) return { type: 'deny', toastKey: 'kanban.denyNotExecutorTakeInWork' };
        return { type: 'status', status: TaskTypeStatus.INWORK, toastKey: 'kanban.messTakenInWork' };
    }

    if (fromStatus === TaskTypeStatus.INWORK && toStatus === TaskTypeStatus.COMPLETED) {
        if (!isExecutor) return { type: 'deny', toastKey: 'kanban.denyNotExecutorComplete' };
        if (isController && autoAcceptOwn) {
            return { type: 'openResponse', toastKey: 'kanban.hintEnterAnswerWillAccept' };
        }
        return { type: 'openResponse', toastKey: 'kanban.hintEnterAnswer' };
    }

    if (fromStatus === TaskTypeStatus.INWORK && toStatus === TaskTypeStatus.ACCEPTED) {
        if (!isExecutor) {
            return { type: 'deny', toastKey: 'kanban.denyNotExecutorAcceptDirect' };
        }
        if (!isController) {
            return { type: 'deny', toastKey: 'kanban.denyNeedControllerAcceptDirect' };
        }
        if (!autoAcceptOwn) {
            // Without auto-accept: complete first, then accept manually
            return { type: 'deny', toastKey: 'kanban.denyInvalidMove' };
        }
        return { type: 'openResponse', toastKey: 'kanban.hintEnterAnswerWillAccept' };
    }

    if (fromStatus === TaskTypeStatus.COMPLETED && toStatus === TaskTypeStatus.ACCEPTED) {
        if (!isController) return { type: 'deny', toastKey: 'kanban.denyNotControllerAccept' };
        return {
            type: 'status',
            status: TaskTypeStatus.ACCEPTED,
            toastKey: 'kanban.messAccepted',
        };
    }

    if (fromStatus === TaskTypeStatus.COMPLETED && toStatus === TaskTypeStatus.INWORK) {
        if (!isController) return { type: 'deny', toastKey: 'kanban.denyNotControllerReturn' };
        return { type: 'openReject', toastKey: 'kanban.hintEnterReject' };
    }

    return { type: 'deny', toastKey: 'kanban.denyInvalidMove' };
};
